import { useEffect, useState } from "react";
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Card,
  CardHeader,
  CardBody,
  Select,
  HStack,
  VStack,
  Text,
  useColorModeValue,
  Spinner,
  Center,
  Badge,
  Divider,
  Icon,
  Flex,
  Button,
  Input,
  FormControl,
  FormLabel,
  useToast,
} from "@chakra-ui/react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  FiTrendingUp,
  FiTrendingDown,
  FiUsers,
  FiTruck,
  FiMapPin,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiRefreshCw,
  FiCalendar,
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import axiosInstance from "../../configs/axios.config";
import { Loading } from "../../components/Loading";
const SERVER = import.meta.env.VITE_API_URL;

const Stats = () => {
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("month");
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });
  const [isCustomDateRange, setIsCustomDateRange] = useState(false);

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const secondaryBg = useColorModeValue("gray.50", "gray.900");
  const toast = useToast();

  /**
   * Validates custom date range selection
   * @returns {boolean} - Whether the date range is valid
   */
  const validateDateRange = () => {
    if (!isCustomDateRange) return true;
    
    if (!dateRange.startDate || !dateRange.endDate) {
      toast({
        title: "Invalid Date Range",
        description: "Please select both start and end dates",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return false;
    }

    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);

    if (start > end) {
      toast({
        title: "Invalid Date Range",
        description: "Start date must be before end date",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return false;
    }

    if (end > new Date()) {
      toast({
        title: "Invalid Date Range",
        description: "End date cannot be in the future",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
      return false;
    }

    return true;
  };

  /**
   * Handles period selection change
   */
  const handlePeriodChange = (newPeriod) => {
    if (newPeriod === "custom") {
      setIsCustomDateRange(true);
      setPeriod("month"); // Keep a default for API
    } else {
      setIsCustomDateRange(false);
      setDateRange({ startDate: "", endDate: "" });
      setPeriod(newPeriod);
    }
  };

  /**
   * Handles date input changes with validation
   */
  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  /**
   * Applies custom date range filter
   */
  const applyCustomDateRange = () => {
    if (validateDateRange()) {
      fetchStats();
    }
  };

  /**
   * Clears custom date range and resets to monthly view
   */
  const clearDateRange = () => {
    setDateRange({ startDate: "", endDate: "" });
    setIsCustomDateRange(false);
    setPeriod("month");
  };

  // Color palette for charts
  const COLORS = {
    primary: "#3B82F6",
    success: "#10B981",
    warning: "#F59E0B",
    danger: "#EF4444",
    purple: "#8B5CF6",
    cyan: "#06B6D4",
    pink: "#EC4899",
    indigo: "#6366F1",
  };

  const fetchStats = async () => {
    // Validate date range before making API call
    if (isCustomDateRange && !validateDateRange()) {
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("period", period);
      
      // Only add date params if custom range is active and valid
      if (isCustomDateRange && dateRange.startDate && dateRange.endDate) {
        params.append("startDate", dateRange.startDate);
        params.append("endDate", dateRange.endDate);
      }

      const response = await axiosInstance.get(`${SERVER}/api/stats/overview?${params.toString()}`);
      setStatsData(response.data.data);
      
      if (isCustomDateRange) {
        toast({
          title: "Stats Updated",
          description: `Showing data from ${new Date(dateRange.startDate).toLocaleDateString()} to ${new Date(dateRange.endDate).toLocaleDateString()}`,
          status: "success",
          duration: 2000,
          isClosable: true,
          position: "top-right",
        });
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast({
        title: "Error",
        description: "Failed to fetch statistics. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only auto-fetch when period changes, not on individual date changes
    if (!isCustomDateRange) {
      fetchStats();
    }
  }, [period]);

  // Initial load
  useEffect(() => {
    fetchStats();
  }, []);

  const StatCard = ({ title, value, change, icon, color, subtitle }) => (
    <Card bg={bgColor} borderWidth="1px" borderColor={borderColor} shadow="sm" _hover={{ shadow: "md" }} transition="all 0.3s">
      <CardBody>
        <HStack justify="space-between" mb={2}>
          <VStack align="start" spacing={0}>
            <Text fontSize="sm" color="gray.500" fontWeight="medium">
              {title}
            </Text>
            <Heading size="lg" mt={1}>
              {value}
            </Heading>
          </VStack>
          <Flex
            bg={`${color}.50`}
            p={3}
            borderRadius="xl"
            color={`${color}.500`}
          >
            <Icon as={icon} boxSize={6} />
          </Flex>
        </HStack>
        {subtitle && (
          <Text fontSize="xs" color="gray.500" mt={2}>
            {subtitle}
          </Text>
        )}
        {change !== undefined && (
          <HStack mt={2}>
            <StatArrow type={change >= 0 ? "increase" : "decrease"} />
            <Text fontSize="sm" color={change >= 0 ? "green.500" : "red.500"} fontWeight="medium">
              {Math.abs(change)}%
            </Text>
            <Text fontSize="sm" color="gray.500">
              vs last period
            </Text>
          </HStack>
        )}
      </CardBody>
    </Card>
  );

  if (loading || !statsData) {
    return (
     
       <Loading />
    );
  }

  const { overview, trends } = statsData;

  // Prepare pie chart data
  const bookingStatusData = [
    { name: "Confirmed", value: overview.bookings.confirmed, color: COLORS.success },
    { name: "Completed", value: overview.bookings.completed, color: COLORS.primary },
    { name: "Cancelled", value: overview.bookings.cancelled, color: COLORS.danger },
  ];

  const fleetStatusData = [
    { name: "Available", value: overview.fleet.available, color: COLORS.success },
    { name: "In Service", value: overview.fleet.inService, color: COLORS.warning },
    { name: "Maintenance", value: overview.fleet.maintenance, color: COLORS.danger },
  ];

  const paymentStatusData = [
    { name: "Successful", value: overview.payments.successful, color: COLORS.success },
    { name: "Pending", value: overview.payments.pending, color: COLORS.warning },
    { name: "Failed", value: overview.payments.failed, color: COLORS.danger },
  ];

  return (
    <Container maxW="full" p={6}>
      {/* Header */}
      <VStack spacing={6} align="stretch" mb={8}>
        <Flex justify="space-between" align="center">
          <VStack align="start" spacing={1}>
            <Heading size="xl">Analytics Dashboard</Heading>
            <Text color="gray.500">Comprehensive overview of all modules</Text>
          </VStack>
          <Button leftIcon={<FiRefreshCw />} onClick={fetchStats} colorScheme="blue" variant="outline" isLoading={loading}>
            Refresh
          </Button>
        </Flex>

        {/* Advanced Filters */}
        <Card bg={bgColor} borderWidth="1px" borderColor={borderColor} shadow="sm">
          <CardBody>
            <VStack spacing={4} align="stretch">
              <HStack spacing={4} align="end" flexWrap="wrap">
                <FormControl maxW="200px">
                  <FormLabel fontSize="sm" fontWeight="semibold" mb={2}>
                    <HStack spacing={2}>
                      <Icon as={FiCalendar} />
                      <Text>Time Period</Text>
                    </HStack>
                  </FormLabel>
                  <Select 
                    value={isCustomDateRange ? "custom" : period} 
                    onChange={(e) => handlePeriodChange(e.target.value)}
                    size="md"
                  >
                    <option value="day">Daily</option>
                    <option value="week">Weekly</option>
                    <option value="month">Monthly</option>
                    <option value="year">Yearly</option>
                    <option value="custom">Custom Range</option>
                  </Select>
                </FormControl>

                {isCustomDateRange && (
                  <>
                    <FormControl maxW="220px">
                      <FormLabel fontSize="sm" fontWeight="semibold" mb={2}>
                        Start Date
                      </FormLabel>
                      <Input
                        type="date"
                        value={dateRange.startDate}
                        onChange={(e) => handleDateChange("startDate", e.target.value)}
                        max={dateRange.endDate || new Date().toISOString().split('T')[0]}
                        size="md"
                      />
                    </FormControl>

                    <FormControl maxW="220px">
                      <FormLabel fontSize="sm" fontWeight="semibold" mb={2}>
                        End Date
                      </FormLabel>
                      <Input
                        type="date"
                        value={dateRange.endDate}
                        onChange={(e) => handleDateChange("endDate", e.target.value)}
                        min={dateRange.startDate}
                        max={new Date().toISOString().split('T')[0]}
                        size="md"
                      />
                    </FormControl>

                    <Button
                      colorScheme="blue"
                      onClick={applyCustomDateRange}
                      isLoading={loading}
                      loadingText="Applying"
                      size="md"
                      mt={6}
                    >
                      Apply
                    </Button>

                    <Button
                      variant="ghost"
                      onClick={clearDateRange}
                      size="md"
                      mt={6}
                    >
                      Clear
                    </Button>
                  </>
                )}
              </HStack>

              {isCustomDateRange && dateRange.startDate && dateRange.endDate && (
                <Badge colorScheme="blue" fontSize="sm" p={2} borderRadius="md">
                  Viewing: {new Date(dateRange.startDate).toLocaleDateString("en-US", { 
                    year: "numeric", 
                    month: "short", 
                    day: "numeric" 
                  })} - {new Date(dateRange.endDate).toLocaleDateString("en-US", { 
                    year: "numeric", 
                    month: "short", 
                    day: "numeric" 
                  })}
                </Badge>
              )}
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Key Metrics Grid */}
      <VStack spacing={8} align="stretch">
        {/* Bookings & Revenue Section */}
        <Box>
          <Heading size="md" mb={4}>
            Bookings & Revenue
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            <StatCard
              title="Total Bookings"
              value={overview.bookings.total}
              icon={FiMapPin}
              color="blue"
              subtitle={`${overview.bookings.confirmed} confirmed • ${overview.bookings.completed} completed`}
            />
            <StatCard
              title="Total Revenue"
              value={`₹${(overview.revenue.total / 1000).toFixed(1)}K`}
              icon={FaRupeeSign}
              color="green"
              subtitle={`Avg: ₹${overview.revenue.average}`}
            />
            <StatCard
              title="Lead Conversion"
              value={`${overview.bookings.conversionRate}%`}
              icon={FiTrendingUp}
              color="purple"
              subtitle={`${overview.leads.converted} of ${overview.leads.total} leads`}
            />
            <StatCard
              title="Active Customers"
              value={overview.customers.active}
              icon={FiUsers}
              color="cyan"
              subtitle={`Total: ${overview.customers.total}`}
            />
          </SimpleGrid>
        </Box>

        {/* Charts Section - Trends */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          <Card bg={bgColor} borderWidth="1px" borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">Booking Trends</Heading>
              <Text fontSize="sm" color="gray.500">
                Bookings over time by status
              </Text>
            </CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={trends.bookings}>
                  <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                  <XAxis dataKey="period" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip contentStyle={{ background: bgColor, border: `1px solid ${borderColor}` }} />
                  <Legend />
                  <Area type="monotone" dataKey="confirmed" stackId="1" stroke={COLORS.success} fill={COLORS.success} fillOpacity={0.6} />
                  <Area type="monotone" dataKey="completed" stackId="1" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.6} />
                  <Area type="monotone" dataKey="cancelled" stackId="1" stroke={COLORS.danger} fill={COLORS.danger} fillOpacity={0.6} />
                </AreaChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          <Card bg={bgColor} borderWidth="1px" borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">Revenue Trends</Heading>
              <Text fontSize="sm" color="gray.500">
                Revenue and average booking value
              </Text>
            </CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trends.revenue}>
                  <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                  <XAxis dataKey="period" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip contentStyle={{ background: bgColor, border: `1px solid ${borderColor}` }} />
                  <Legend />
                  <Line type="monotone" dataKey="revenue" stroke={COLORS.success} strokeWidth={3} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="average" stroke={COLORS.cyan} strokeWidth={2} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Fleet & Drivers Section */}
        <Box>
          <Heading size="md" mb={4}>
            Fleet & Drivers
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            <StatCard
              title="Total Drivers"
              value={overview.drivers.total}
              icon={FiUsers}
              color="blue"
              subtitle={`${overview.drivers.active} active • ${overview.drivers.onTrip} on trip`}
            />
            <StatCard
              title="Total Fleet"
              value={overview.fleet.total}
              icon={FiTruck}
              color="purple"
              subtitle={`${overview.fleet.available} available • ${overview.fleet.inService} in service`}
            />
            <StatCard
              title="Idle Drivers"
              value={overview.drivers.idle}
              icon={FiClock}
              color="warning"
              subtitle="Available for assignments"
            />
            <StatCard
              title="Vendors"
              value={overview.vendors.total}
              icon={FiCheckCircle}
              color="cyan"
              subtitle={`${overview.vendors.active} active`}
            />
          </SimpleGrid>
        </Box>

        {/* Pie Charts Section */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <Card bg={bgColor} borderWidth="1px" borderColor={borderColor}>
            <CardHeader>
              <Heading size="sm">Booking Status Distribution</Heading>
            </CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={bookingStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {bookingStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          <Card bg={bgColor} borderWidth="1px" borderColor={borderColor}>
            <CardHeader>
              <Heading size="sm">Fleet Status Distribution</Heading>
            </CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={fleetStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {fleetStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>

          <Card bg={bgColor} borderWidth="1px" borderColor={borderColor}>
            <CardHeader>
              <Heading size="sm">Payment Status Distribution</Heading>
            </CardHeader>
            <CardBody>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={paymentStatusData}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    outerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {paymentStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Lead Conversion Chart */}
        <Card bg={bgColor} borderWidth="1px" borderColor={borderColor}>
          <CardHeader>
            <Heading size="md">Lead Conversion Analysis</Heading>
            <Text fontSize="sm" color="gray.500">
              Track lead conversion and expiration rates
            </Text>
          </CardHeader>
          <CardBody>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={trends.leadConversion}>
                <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                <XAxis dataKey="period" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip contentStyle={{ background: bgColor, border: `1px solid ${borderColor}` }} />
                <Legend />
                <Bar dataKey="total" fill={COLORS.primary} name="Total Leads" radius={[8, 8, 0, 0]} />
                <Bar dataKey="converted" fill={COLORS.success} name="Converted" radius={[8, 8, 0, 0]} />
                <Bar dataKey="expired" fill={COLORS.danger} name="Expired" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardBody>
        </Card>

        {/* Additional Metrics */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          <Card bg={bgColor} borderWidth="1px" borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">Lead Summary</Heading>
            </CardHeader>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between">
                  <Text fontWeight="medium">Total Leads</Text>
                  <Badge colorScheme="blue" fontSize="md" px={3} py={1}>
                    {overview.leads.total}
                  </Badge>
                </HStack>
                <Divider />
                <HStack justify="space-between">
                  <Text color="gray.600">New</Text>
                  <Text fontWeight="bold" color="cyan.500">
                    {overview.leads.new}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Converted</Text>
                  <Text fontWeight="bold" color="green.500">
                    {overview.leads.converted}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Expired</Text>
                  <Text fontWeight="bold" color="red.500">
                    {overview.leads.expired}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Pending</Text>
                  <Text fontWeight="bold" color="orange.500">
                    {overview.leads.pending}
                  </Text>
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          <Card bg={bgColor} borderWidth="1px" borderColor={borderColor}>
            <CardHeader>
              <Heading size="md">Payment Summary</Heading>
            </CardHeader>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between">
                  <Text fontWeight="medium">Total Transactions</Text>
                  <Badge colorScheme="purple" fontSize="md" px={3} py={1}>
                    {overview.payments.total}
                  </Badge>
                </HStack>
                <Divider />
                <HStack justify="space-between">
                  <Text color="gray.600">Successful</Text>
                  <Text fontWeight="bold" color="green.500">
                    {overview.payments.successful}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Pending</Text>
                  <Text fontWeight="bold" color="orange.500">
                    {overview.payments.pending}
                  </Text>
                </HStack>
                <HStack justify="space-between">
                  <Text color="gray.600">Failed</Text>
                  <Text fontWeight="bold" color="red.500">
                    {overview.payments.failed}
                  </Text>
                </HStack>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>
      </VStack>
    </Container>
  );
};

export default Stats;
